import { useContext, useEffect, useRef, useState } from "react";
import {
  CalcInput,
  ParagraphText,
  UBButton,
  UBInput,
  UBRadioBox,
} from "../../../../Libraries";
import {
  FileAttachment,
  RouteLeavingGuard,
  TitleAndActionBox,
} from "../../../common";
import BusinessLoader from "../../../common/BusinessLoader";
import { LanguageContext } from "../../../../context/LanguageContext";
import { Box, capitalize, Chip, CircularProgress, Grid } from "@mui/material";
import { useSnackbar } from "../../../../context/SnackbarContext";
import saleStyles from "../../SaleExpense/SaleExpenseForm/styles.module.css";
import { EnterIcon } from "../../../../assets/icons/icon";
import { PaymentMode, sentenceCase } from "../../../../constants";
import { useLocation, useNavigate } from "react-router-dom";
import withObservables from "@nozbe/with-observables";
import {
  calculateTotalOutstanding,
  getCustomerByID,
} from "../../../../database/helpers/customer";
import StockAddItem from "../../../common/StockAddItem";
import moment from "moment";
import {
  getPaymentByID,
  observePaymentsById,
} from "../../../../database/helpers/payment";

import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { useAuthContext } from "../../../../context/AuthContext";
import {
  bulkCreateOrdersV3,
  bulkDeleteOrders,
  bulkRestoreStock,
  bulkUpdateOrders,
  bulkUpdateTransactionStock,
} from "../../../../database/helpers/orders";
import { bulkUpdateStockV2 } from "../../../../database/helpers/stock";
import {
  bulkCreateInventoryV3,
  bulkDeleteInventory,
  bulkUpdateInventory,
} from "../../../../database/helpers/inventory";
import { formatNum, uuid } from "../../../../oscar-pos-core/constants";
import { Sync } from "../../../../SyncService";
import { of, switchMap } from "rxjs";
import { blurAllInputField, getExtensionForImageUrl, getCategoriesByType, resolveCategoryDisplayName, removeRsFromValue } from "../../../../utils";
import { useDemoModeNavigation } from "../../../../hooks/useDemoNavigation";

import TabAutoComplete from "../../../../Libraries/TabAutoComplete";

const EnhancementTransactionForm = ({ customer, txnsDetail, orders }) => {
  customer = customer.length ? customer[0] : customer;
  txnsDetail = txnsDetail?.length ? txnsDetail[0] : null;

  const [I18n] = useContext(LanguageContext);
  const { user, business } = useAuthContext();
  const { handleDemoNavigation } = useDemoModeNavigation();
  const [isDirty, setDirty] = useState(false);
  const [itemObj, setItemObj] = useState({});
  const [state, setState] = useState({
    paymentMode: txnsDetail?.payment_mode || PaymentMode.Jama,
    amount: txnsDetail?.amount || "",
    date: txnsDetail?.transacted_at
      ? moment(txnsDetail?.transacted_at)
      : moment(),
    note: txnsDetail?.udhaar_note || "",
    image: txnsDetail?.image_url || "",
    items: [],
    category: txnsDetail?.category || "",
    categoryLabel:
      resolveCategoryDisplayName(
        txnsDetail?.payment_mode === PaymentMode.Udhaar
          ? "accounts_expense"
          : "accounts_sale",
        txnsDetail?.category,
      ) || "",
  });
  const [files, setFiles] = useState(
    txnsDetail?.image_url
      ? [
        {
          name: `image.${getExtensionForImageUrl(txnsDetail?.image_url)}`,
          file: txnsDetail?.image_url,
        },
      ]
      : txnsDetail?.attachment || [],
  ); // Tracks the files selected in FileAttachment
  const [isLoading, setIsLoading] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [categoryOpen, setCategoryOpen] = useState(false);

  const { showSnackbar } = useSnackbar();
  const inputAmountRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = Boolean(txnsDetail);
  const onChangeText = (key, text) => {
    if (key === "amount") {
      const result = removeRsFromValue(`${text}`);
      if (result.length >= 1 && state.amount.length === 0)
        triggerLogEvent(analytics, "acc_amount_start");
      setState((preState) => ({
        ...preState,
        [key]: result,
      }));
      setDirty(true);
      return;
    }
    setDirty(true);
    if (key === "note" && text.length >= 1 && state.note.length === 0)
      triggerLogEvent(analytics, "acc_note_start");
    setState((preState) => ({
      ...preState,
      [key]: text,
    }));
  };

  useEffect(() => {
    if (inputAmountRef?.current) inputAmountRef.current.focus();
    if (location?.state?.paymentMode)
      setState((e) => ({ ...e, paymentMode: location.state.paymentMode }));
  }, [location]);

  useEffect(() => {
    if (orders?.length > 0) {
      const items = {};
      const itemsArr = [];

      orders.forEach((element) => {
        const obj = {
          cost_price: element.cost_price,
          id: element.stock.id,
          name: element.name,
          quantity: element.quantity,
          selling_price: element.selling_price,
        };
        items[element.stock.id] = obj;
        itemsArr.push(obj);
      });
      setItemObj(items);
      setState((e) => ({ ...e, items: itemsArr }));
    }
  }, [orders]);

  useEffect(() => {
    setCategoryList(
      getCategoriesByType(
        state.paymentMode == PaymentMode.Udhaar ? "accounts_expense" : "accounts_sale",
      ),
    );
  }, [state.paymentMode]);

  const handleEditTransactionItems = async (selectedItems, tranType) => {
    let toAdd = [],
      toEdit = [],
      toDelete = [];
    let orderMap = {};

    await Promise.all(orders.map(async (order) => {
      const matchingItem = selectedItems.find((i) => i.id === order.stock.id);

      if (matchingItem) {
        toEdit.push({ original: order, updated: matchingItem });
      } else {
        const stock = await order.stock.fetch();
        toDelete.push({ original: order, updated: stock });
      }

      orderMap[order.stock.id] = order;
    }));

    selectedItems.forEach((x) => {
      if (!orderMap[x.id]) {
        toAdd.push(x);
      }
    });

    // Handle adding new items
    if (toAdd.length) {
      const ordersByItemId = await bulkCreateOrdersV3(
        business,
        txnsDetail,
        toAdd,
        state.paymentMode === PaymentMode.Jama ? "out" : "in",
        tranType,
      );
      await bulkUpdateStockV2(
        toAdd,
        state.paymentMode === PaymentMode.Jama ? "out" : "in",
      );
      await bulkCreateInventoryV3(
        business,
        txnsDetail,
        toAdd,
        state.paymentMode === PaymentMode.Jama ? "in" : "out",
        "payment",
        ordersByItemId
      );
    }

    // Handle editing existing items
    if (toEdit.length) {
      await bulkUpdateTransactionStock(
        toEdit,
        state.paymentMode === PaymentMode.Jama ? "out" : "in",
      );
      await bulkUpdateInventory(
        business,
        txnsDetail,
        toEdit,
        state.paymentMode === PaymentMode.Jama ? "out" : "in",
        "payment",
      );
      await bulkUpdateOrders(toEdit);
    }
    // Handle deleting items
    if (toDelete.length) {
      await bulkRestoreStock(
        toDelete,
        state.paymentMode === PaymentMode.Jama ? "out" : "in",
      );
      await bulkDeleteOrders(toDelete);
      await bulkDeleteInventory(txnsDetail, toDelete, state.paymentMode === PaymentMode.Jama ? "out" : "in", 'payment');
    }
  };

  const onSave = async () => {
    if (isLoading) return;
    setIsLoading(true);
    const filteredItems = state.items.filter((item) => item.isNew);
    if (filteredItems.length) {
      await Promise.all(
        filteredItems.map(async (item) => {
          const convertedObj = {
            name: item.name,
            stock: 0,
            cost_price:
              state.paymentMode === PaymentMode.Jama
                ? Number(item.cost_price)
                : 0,
            selling_price:
              state.paymentMode === PaymentMode.Udhaar
                ? Number(item.selling_price)
                : 0,
            unit: "Pieces",
            id: item.id,
          };
          return await business
            .createStock(convertedObj)
            .then(async () => {
              //   triggerLogEvent(analytics, type === "in" ? "inv_stock_created" : "pb_stock_created");
            })
            .catch((err) => {
              setIsLoading(false);
            });
        }),
      )
        .then(async (valu) => {
          await Sync(user?.phone_number, navigate);
        })
        .catch((err) => {
          setIsLoading(false);
        });
    }
    if (txnsDetail) {
      try {
        const dataToEdit = {
          amount: Math.floor(Number(state.amount)),
          udhaar_note: state.note,
          date: state.date ? new Date(state.date) : new Date(),
          attachment: files,
          category: state?.category,
        };

        if (dataToEdit.amount !== txnsDetail.amount)
          triggerLogEvent(analytics, "acc_txn_edit_amount");
        if (!moment(dataToEdit.date).isSame(moment(txnsDetail?.transacted_at)))
          triggerLogEvent(analytics, "acc_txn_edit_date");
        if (dataToEdit.udhaar_note !== txnsDetail.udhaar_note)
          triggerLogEvent(analytics, "acc_txn_edit_notes");
        if (state.items.length !== orders.length)
          triggerLogEvent(analytics, "acc_txn_edit_add_items");
        if (
          dataToEdit.image_url &&
          dataToEdit.image_url !== txnsDetail?.image_url
        )
          triggerLogEvent(analytics, "acc_txn_edit_photo");
        if (!dataToEdit.image_url && txnsDetail?.image_url)
          triggerLogEvent(analytics, "acc_txn_edit_photo_delete");
        await txnsDetail.updatePayment(dataToEdit);
        let totalOutstanding = await calculateTotalOutstanding(customer);

        await customer.updateTotalOutstanding(totalOutstanding);
        await handleEditTransactionItems(state.items, "payment");

        triggerLogEvent(analytics, "acc_txn_edit", {
          newAmount: state.amount,
          newNote: state.note || "",
          newDate: state.date,
          added_items: state.items.filter(
            (i) => !orders.some((o) => o.stock.id === i.id),
          ),
        });
        setDirty(false);
        Sync(user?.phone_number, navigate);
        setIsLoading(false);
        showSnackbar({
          message: I18n.transaction_updated_successfully,
          severity: "success",
        });
        setTimeout(
          () => navigate(`/Accounts/AccountDetail/${customer.id}`),
          1000,
        );
      } catch (error) {
        setIsLoading(false);
      }
    } else {
      let paramsForPayment = {
        uid: uuid(),
        amount: +state.amount,
        payment_mode: state.paymentMode,
        date: state.date ? new Date(state.date) : new Date(),
        udhaar_note: state.note,
        imageURL: null,
        attachment: files,
        category: state?.category,

        outstanding:
          state.paymentMode === PaymentMode.Jama
            ? customer.total_outstanding_payment - Number(state.amount)
            : customer.total_outstanding_payment + Number(state.amount),
      };
      if (paramsForPayment.amount)
        triggerLogEvent(analytics, "acc_amount_added");
      if (paramsForPayment.date) triggerLogEvent(analytics, "acc_date_set");
      if (paramsForPayment.udhaar_note)
        triggerLogEvent(analytics, "acc_note_added");
      if (state.items.length) triggerLogEvent(analytics, "acc_item_added");
      if (paramsForPayment.imageURL)
        triggerLogEvent(analytics, "acc_photo_added");

      await customer.createPayment(paramsForPayment);
      let payments = await customer.payment.fetch();
      if (payments.length == 5) {
        triggerLogEvent(analytics, "user_activated", {
          user: user.phone_number,
        });
        triggerLogEvent(analytics, "acc_PU", {
          user: user.phone_number,
        });
        window.fbq("track", "Subscribe");
      }

      let payment = await getPaymentByID(paramsForPayment.uid);
      if (state.items.length) {
        const ordersByItemId = await bulkCreateOrdersV3(
          business,
          payment,
          state.items,
          state.paymentMode === PaymentMode.Jama ? "out" : "in",
          "payment",
        );

        await bulkUpdateStockV2(
          state.items,
          state.paymentMode === PaymentMode.Jama ? "out" : "in",
        );
        await bulkCreateInventoryV3(
          business,
          payment,
          state.items,
          state.paymentMode === PaymentMode.Jama ? "in" : "out",
          "payment",
          ordersByItemId
        );
      }

      try {
        setDirty(false);
        Sync(user?.phone_number, navigate);
        setIsLoading(false);
        triggerLogEvent(analytics, "acc_transaction_added");
        showSnackbar({
          message: I18n.transaction_added_successfully,
          severity: "success",
        });
        // --- DEMO MODE LOGIC ---
        handleDemoNavigation(
          "transaction",
          `/Accounts/AccountDetail/${customer.id}`,
          {},
          1000,
        );
      } catch (err) {
        setIsLoading(false);
      }
    }
  };

  const handleAddItem = (items) => {
    if (isEditMode === true) {
      triggerLogEvent(analytics, "acc_txn_edit_add_items", {});
    } else {
      triggerLogEvent(analytics, "acc_item_added", {});
    }

    setDirty(true);
    setState((e) => ({ ...e, items }));
  };
  const isDisabled =
    state.amount === undefined ||
    state.amount == null ||
    state.amount == "" ||
    isLoading;

  let top2Cate = [...categoryList];
  top2Cate = top2Cate.splice(0, 2);

  return (
    <Grid
      container
      className={`${saleStyles.root} ${saleStyles.SaleExpenseFormWrapper} TransactionForm`}
    >
      <BusinessLoader />
      <Grid item md={12}>
        <TitleAndActionBox
          data={{
            id: "transactionForm",
            title: `${I18n.transaction_for} ${capitalize(customer?.name || "")}`,
            isBack: true,
            onBack: () => navigate(-1),
            isTitleCase: false,
          }}
        />
        <Box className={saleStyles.formBodyGrid}>
          <Box className={saleStyles.formColumn}>
            <Box
              className={`${saleStyles.formBody} expenseFormBody`}>
              <ParagraphText
                className={"fontSize14 newFontColor mb-2 InputLabelStyle"}
              >
                {sentenceCase(I18n.transaction_type)}
              </ParagraphText>
              <Box className={saleStyles.paymentStatusBoxWrapper}>
                <Box className={saleStyles.paymentStatusBox}>
                  <Box className={saleStyles.paymentStatusBoxLeft}>
                    <UBRadioBox
                      disabled={Boolean(txnsDetail)}
                      label={I18n.itook}
                      onChange={() => {
                        setState((e) => ({
                          ...e,
                          paymentMode: PaymentMode.Jama,
                          category: "",
                          categoryLabel: "",
                        }));
                        setDirty(true);
                      }}
                      value={
                        state.paymentMode === PaymentMode.Jama ? I18n.itook : ""
                      }
                    />
                    <UBRadioBox
                      disabled={Boolean(txnsDetail)}
                      label={I18n.igave}
                      onChange={() => {
                        setState((e) => ({
                          ...e,
                          paymentMode: PaymentMode.Udhaar,
                          category: "",
                          categoryLabel: "",
                        }));
                        setDirty(true);
                      }}
                      value={
                        state.paymentMode === PaymentMode.Udhaar ? I18n.igave : ""
                      }
                    />
                  </Box>
                </Box>
              </Box>

              <Box className={saleStyles.expenseAmountInputBox}>
                <CalcInput
                  inputId="transaction_amount"
                  baseClassname={saleStyles.amountInput}
                  placeholder={I18n.amount}
                  onChange={onChangeText}
                  label={sentenceCase(I18n.enter_amount_trans)}
                  required={true}
                  name={"amount"}
                  value={state.amount}
                  inputRef={inputAmountRef}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isDisabled) {
                      blurAllInputField();
                      onSave();
                    }
                  }}
                  infoLabel={
                    <ParagraphText className={"fontSize12"}>
                      {I18n.balance}:{" "}
                      <span
                        className={
                          customer?.total_outstanding_payment === 0
                            ? "foundationInk600Color"
                            : (customer?.total_outstanding_payment || 0) < 0
                              ? "foundationGreenColor"
                              : "foundationOrangeColor"
                        }
                      >
                        {`Rs. ${formatNum(
                          Math.abs(customer?.total_outstanding_payment || 0),
                        )}`}
                      </span>{" "}
                    </ParagraphText>
                  }
                />
              </Box>

              <Grid
                container
                spacing={2}
                className={`${saleStyles.formBodyGridRow} ${saleStyles.formBodyGridRowCategory}`}
              >
                <Grid item md={12}>
                  <Box
                    className={`${saleStyles.tabAutoCompleteBox} ${saleStyles.categoryAutoComplete} tabAutoCompleteBox categoryAutoComplete`}
                  >
                    <TabAutoComplete
                      id={"category_forP&L_tracking"}
                      list={categoryList}
                      label={I18n.category_pl_tracking}
                      handleSelect={(item) => {
                        setState((e) => ({
                          ...e,
                          category: item?.id || "",
                          categoryLabel: item?.label || "",
                        }));
                      }}
                      value={state.categoryLabel}
                      placeholder={I18n.category_pl_tracking
                      }
                      handleStartLogEvent={
                        isEditMode
                          ? () => { }
                          : () =>
                            triggerLogEvent(
                              analytics,
                              "transaction_category_start",
                            )
                      }
                      handleManuallyInput={() => setCategoryOpen(false)}
                      manuallyFocusInput={categoryOpen}
                    />
                    <Box className={saleStyles.categoryInputBoxChip}>
                      {top2Cate.length > 1 &&
                        top2Cate.map((item) => (
                          <Chip
                            key={item.label || item.value}
                            className={saleStyles.categoryInputBoxChipItem}
                            onClick={() =>
                              setState((e) => ({
                                ...e,
                                category: item?.id || "",
                                categoryLabel: item?.label || "",
                              }))
                            }
                            label={item.label}
                          />
                        ))}
                      <span
                        onClick={() => setCategoryOpen(true)}
                        className="cursorPointer barclayBlueColor fontSize12 textDecorationUnerline"
                      >
                        {I18n.see_more}
                      </span>
                    </Box>
                  </Box>
                </Grid>
                <Grid item md={12}>
                  <Box className={saleStyles.noteInputBox}>
                    <UBInput
                      inputId="transaction_note"
                      name="note"
                      label={I18n.notes}
                      placeholder={I18n.note_placeholder}
                      onChange={onChangeText}
                      value={state.note}
                      multiline
                      rows={1}
                      maxLength={500}
                      baseClassname={saleStyles.noteInput}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Box className={saleStyles.attachmentAndDateStyle}>
                <FileAttachment
                  id="transaction_file"
                  filesPath={files}
                  setFilesPath={(f) => {
                    setDirty(true);
                    setFiles(f);
                  }}
                  handleLogEvent={
                    isEditMode
                      ? () => { }
                      : () => triggerLogEvent(analytics, "acc_photo_start")
                  }
                  showDate
                  selectedDate={state.date}
                  setSelectedDate={(date) => {
                    setState((e) => ({ ...e, date }));
                    setDirty(true);
                    if (isEditMode) {
                      triggerLogEvent(analytics, "acc_txn_edit_date", {
                        oldDate: txnsDetail?.transacted_at,
                        newDate: date,
                      });
                    }
                  }}
                  datePlaceholder={I18n.select_date}
                  isEditMode={isEditMode}
                  handleDateLogEvent={() =>
                    triggerLogEvent(analytics, "acc_date_start")
                  }
                />
              </Box>

              <Box className={saleStyles.addItemBox}>
                <StockAddItem
                  id="transactionAddStock"
                  itemObj={itemObj}
                  handleAddItem={handleAddItem}
                  updateAmountWithTotal={(total) =>
                    onChangeText("amount", total)
                  }
                  isITook={state.paymentMode === PaymentMode.Jama}
                  isDisplayUpdateAmountSection={true}
                  handleStartLogEvent={() =>
                    triggerLogEvent(analytics, "acc_item_start")
                  }
                />
              </Box>

              <UBButton
                id="transactionForm_saveBtn"
                title={<ParagraphText>{I18n.save}</ParagraphText>}
                loading={isLoading}
                iconComponent={<EnterIcon />}
                iconPlacement="right"
                disabled={isDisabled || isLoading}
                className={"buttonPrimary saveSaleButton"}
                onClick={onSave}
              />
            </Box>
          </Box>
        </Box>
      </Grid>
      <RouteLeavingGuard
        id="transactionForm"
        when={isDirty}
        confirmText={I18n.discard_transaction_btn}
        title={I18n.discard_transaction_title}
        message={I18n.this_action_cannot_be_undone}
      />
    </Grid>
  );
};

const enhance = withObservables(
  ["customerId", "txnsId", "business"],
  ({ customerId, txnsId, business }) => ({
    customer: txnsId
      ? observePaymentsById(txnsId).pipe(
        switchMap((txns) =>
          txns.length ? txns[0].customer.observe() : of([]),
        ),
      )
      : getCustomerByID(customerId),
    txnsDetail: txnsId ? observePaymentsById(txnsId) : of([]),
    orders: txnsId
      ? observePaymentsById(txnsId).pipe(
        switchMap((txns) =>
          txns.length ? txns[0].orders.observe() : of([]),
        ),
      )
      : of([]),
  }),
);

export default enhance(EnhancementTransactionForm);
